// @ts-nocheck
/*
 * Port of views/dashboard/incoming-summary/download-report.js (488L). BYTE-IDENTICAL.
 *
 * Async report queue viewer. Lists user-requested campaign report exports
 * with status (Initiated / Processing / Ready-for-Download); ready rows
 * expose a Download button. Goes through `loadPortalReportData` for
 * rate-limited refresh (3-month back-date limit). LottieAnimation modal
 * dropped per v2 policy (plain react-bootstrap Modal with message).
 *
 * V1 QUIRKS preserved verbatim:
 *  - Local state var is `incomingSummary` even though this file is for
 *    Download Report â€” inherited naming, kept.
 *  - `getFirstOfMonth()` is the DEFAULT start; picker allows going back
 *    up to 3 months.
 *  - CSV export uses `filteredData`, not `incomingSummary`.
 *  - `report_type` column uses a static REPORT_TYPE_LABELS map (campaign +
 *    incoming_did / outgoing_did); unknown types (legacy `full`) fall
 *    through as-is.
 */
import React, { useState, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { getApiCall } from '@/service/httpCall';
import Loader from '@/components/partials/components/Loader';
import { API_INCOMING_SUMMARY_LIST, DOWNLOAD_REPORT } from '@/service/url';
import { Storage } from '@/utilities/setting';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  loadPortalReportData,
  PORTAL_REPORT_RESOURCES,
} from '@/utilities/portalReportRefresh';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, RefreshCw } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { usePersistedState } from '@/lib/filters/persisted-state';

const dateToInputStr = (d: any) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const inputStrToLocalDate = (str: any) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(
    parseInt(m[1], 10),
    parseInt(m[2], 10) - 1,
    parseInt(m[3], 10),
  );
};

const getToday = () => new Date();
const getFirstOfMonth = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), 1);
};
const getThreeMonthsAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d;
};

const DownloadReport = () => {
  const [incomingSummary, setDownloadReport] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const [startDate, setStartDate] = usePersistedState<any>('ivr-reports/DownloadReport:startDate', getFirstOfMonth);
  const [endDate, setEndDate] = usePersistedState<any>('ivr-reports/DownloadReport:endDate', getToday);
  const [showToastModal, setShowToastModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [loggedInUserId, setLoggedInUserId] = useState<any>(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState<any>(null);

  const showToasts = (message: any) => {
    toast.success(message);
  };

  const handleFilter = () => {
    fetchDownloadReport(true, startDate, endDate);
  };

  // Re-polls the applied range so Initiated/Processing rows flip to Ready.
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await fetchDownloadReport(true, startDate, endDate);
    } finally {
      setRefreshing(false);
    }
  };

  const applyDownloadReportList = (resp: any) => {
    if (!resp) {
      return;
    }

    const updatedList = resp.map((val: any, idx: number) => {
      const data = val.dataValues || val;
      return {
        ...data,
        key: idx + 1,
      };
    });

    setDownloadReport(updatedList);
    setFilteredData(updatedList);
  };

  const fetchDownloadReport = async (
    force = false,
    start: any = startDate,
    end: any = endDate,
  ) => {
    if (!loggedInUserId || !loggedInUserRole) {
      return;
    }

    const startStr = dateToInputStr(start);
    const endStr = dateToInputStr(end);
    const query = new URLSearchParams();
    if (startStr) query.append('start_date', startStr);
    if (endStr) query.append('end_date', endStr);
    const qs = query.toString();

    try {
      const result: any = await loadPortalReportData({
        resource: PORTAL_REPORT_RESOURCES.DOWNLOAD_REPORT_LIST,
        userId: loggedInUserId,
        role: loggedInUserRole,
        params: { start_date: startStr, end_date: endStr },
        force,
        onBlocked: (remainingMs: number) => {
          toast.info(
            `Download report list can be refreshed after ${Math.ceil(
              remainingMs / 1000,
            )} seconds.`,
          );
        },
        fetchFn: () =>
          getApiCall(`${DOWNLOAD_REPORT}${qs ? `?${qs}` : ''}`),
      });
      applyDownloadReportList(result.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        return;
      }

      try {
        const user = JSON.parse(rawUser);
        setLoggedInUserId(user?.id);
        setLoggedInUserRole(user?.role);
      } catch (error) {
        console.error('Invalid JSON in Storage user:', error);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (loggedInUserId && loggedInUserRole) {
      fetchDownloadReport(false);
    }
  }, [loggedInUserId, loggedInUserRole]);

  const handleReset = () => {
    const start = getFirstOfMonth();
    const end = getToday();
    setStartDate(start);
    setEndDate(end);
    fetchDownloadReport(true, start, end);
  };

  const [
    moduleOperationsForReadPermissionCheck,
    setModuleOperationsForReadPermissionCheck,
  ] = useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const downloadFile = (rowData: any) => {
    if (!rowData || !rowData.download_link) {
      console.error('Download link is missing.');
      return;
    }

    const link = document.createElement('a');
    link.href = rowData.download_link;
    link.download = rowData.download_link.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const reportStatus = (rowData: any) => {
    let label = '';
    let tone = '';
    const noData = rowData.status == 4 || (rowData.status == 2 && !rowData.download_link);
    switch (noData ? '4' : String(rowData.status)) {
      case '0':
        label = 'Initiated';
        tone = 'bg-primary/10 text-primary ring-primary/25';
        break;
      case '1':
        label = 'Processing';
        tone =
          'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300';
        break;
      case '2':
        label = 'Ready for Download';
        tone =
          'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';
        break;
      case '3':
        label = 'Failed';
        tone = 'bg-red-100 text-red-700 ring-red-200 dark:bg-red-950/40 dark:text-red-300';
        break;
      case '4':
        label = 'No Data Found';
        tone = 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300';
        break;
      default:
        return <span className="text-muted-foreground">—</span>;
    }
    return (
      <span
        className={
          'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
          tone
        }
      >
        {label}
      </span>
    );
  };

  const REPORT_TYPE_LABELS: any = {
    campaign_full: 'Campaign Full Report',
    campaign_webhook: 'Campaign Webhook Report',
    incoming_did: 'Incoming DID Report',
    outgoing_did: 'Outgoing DID Report',
    cdr_report: 'CDR Detailed Report',
  };

  const reportTypeLabel = (rowData: any) =>
    REPORT_TYPE_LABELS[rowData?.report_type] || rowData?.report_type || '-';

  const didText = (row: any) => {
    if (row?.did != null) return row.did;
    const f = row?.filters;
    if (!f) return '-';
    return `${f.dids?.length ? f.dids.join(', ') : 'All DIDs'} · ${f.startDate} → ${f.endDate}`;
  };

  const didCell = (row: any) => {
    const f = row?.filters;
    if (row?.did != null || !f) return didText(row);
    const dids: string[] = f.dids || [];
    const shown = dids.length
      ? `${dids.slice(0, 2).join(', ')}${dids.length > 2 ? ` +${dids.length - 2} more` : ''}`
      : 'All DIDs';
    const at = (v: any) => String(v ?? '').slice(0, 16);
    return (
      <div className="max-w-[220px] leading-tight" title={didText(row)}>
        <div className="truncate">{shown}</div>
        <div className="whitespace-nowrap text-[11px] text-muted-foreground">
          {at(f.startDate)} → {at(f.endDate)}
        </div>
      </div>
    );
  };

  const handleExport = async () => {
    try {
      exportToCSV({
        fileName: 'download-report-list',
        data: filteredData,
        columns: [
          {
            header: 'Campaign ID',
            accessor: (row: any) => row.campaign_id ?? '-',
          },
          {
            header: 'Campaign Name',
            accessor: (row: any) => row.campaign_name || '-',
          },
          { header: 'Did', accessor: didText },
          { header: 'Requested By', accessor: (row: any) => row.username || '-' },
          {
            header: 'Schedule Date',
            accessor: (row: any) =>
              row.schedule_date
                ? new Date(row.schedule_date).toLocaleString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                  })
                : '-',
          },
          {
            header: 'Requested Date',
            accessor: (row: any) =>
              new Date(row.requested_date).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
          { header: 'Report Status', accessor: (row: any) => reportStatus(row) },
          {
            header: 'Report Type',
            accessor: (row: any) => reportTypeLabel(row),
          },
        ],
      });
    } finally {
      /* noop */
    }
  };

  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    'Download Report',
    'read',
  );

  if (
    Object.keys(moduleOperationsForReadPermissionCheck).length > 0 &&
    !canRead
  ) {
    return (
      <div
        className="text-center p-4"
        style={{
          marginTop: '50px',
          marginBottom: '50px',
          fontSize: '16px',
        }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'campaign_id',
      header: 'Campaign ID',
      cell: ({ row }: any) => row.original.campaign_id ?? '-',
    },
    {
      accessorKey: 'campaign_name',
      header: 'Campaign Name',
      cell: ({ row }: any) => row.original.campaign_name || '-',
    },
    {
      accessorKey: 'did',
      header: 'Did',
      cell: ({ row }: any) => didCell(row.original),
    },
    {
      accessorKey: 'username',
      header: 'Requested By',
      cell: ({ row }: any) => row.original.username || '-',
    },
    {
      accessorKey: 'schedule_date',
      header: 'Schedule Date',
      cell: ({ row }: any) =>
        row.original.schedule_date
          ? new Date(row.original.schedule_date).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })
          : '-',
    },
    {
      accessorKey: 'requested_date',
      header: 'Requested date',
      cell: ({ row }: any) =>
        new Date(row.original.requested_date).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
    },
    {
      id: 'report_status',
      header: 'Report Status',
      cell: ({ row }: any) => reportStatus(row.original),
    },
    {
      accessorKey: 'report_type',
      header: 'Report type',
      cell: ({ row }: any) => reportTypeLabel(row.original),
    },
    {
      id: 'download',
      header: 'Download Link',
      enableSorting: false,
      cell: ({ row }: any) => {
        const rowData = row.original;
        if (rowData.status == 4 || (rowData.status == 2 && !rowData.download_link)) {
          return <span className="text-muted-foreground">No data found</span>;
        }
        if (rowData.status == 2) {
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                downloadFile(rowData);
              }}
              disabled={!rowData.download_link}
              title="Download report"
              aria-label="Download report"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 ring-1 ring-inset ring-emerald-200 transition-colors hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-emerald-950/40 dark:text-emerald-300"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          );
        }
        return <span className="text-muted-foreground">—</span>;
      },
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Download Reports"
        description="Bulk-downloaded report archives — reprocess or re-download by date range."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="gap-1.5"
              title="Refresh report status"
            >
              <RefreshCw className={`h-3.5 w-3.5${refreshing ? ' animate-spin' : ''}`} />
              Refresh
            </Button>
            {filteredData.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5">
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            )}
          </div>
        }
      />
      <div className="mt-4">
        <DateRangeFilter
          className="mb-3"
          value={{ from: dateToInputStr(startDate), to: dateToInputStr(endDate) }}
          onChange={({ from, to }) => {
            setStartDate(inputStrToLocalDate(from));
            setEndDate(inputStrToLocalDate(to));
          }}
          onApply={handleFilter}
          onReset={handleReset}
          min={dateToInputStr(getThreeMonthsAgo())}
          max={dateToInputStr(getToday())}
        />
        <div className="scroll-container">
          <DataTable
            tableId="download-report:v2"
            columns={columns}
            data={filteredData}
            pageSize={10}
            emptyTitle="No records"
            disableExport
          />
        </div>
      </div>
    </FillPage>
  );
};

export default DownloadReport;
